import { memo, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';

import FormDateField from 'components/ui/form/FormDateField';
import FormSelectField from 'components/ui/form/FormSelectField';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

const PriceCell = ({ value, suffix }) => (
  <NumericFormat
    value={value || 0}
    displayType='text'
    thousandSeparator=' '
    decimalScale={2}
    suffix={` ${suffix}`}
    className='whitespace-nowrap'
  />
);

const EarningsModal = ({ open, setOpen, defaultUserId, defaultFrom, defaultTill }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const fetchEarnings = async (values) => {
    if (!values.user_id) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (values.from) params.append('from', values.from);
      if (values.till) params.append('till', values.till);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await axiosPrivate.get(
        `/admin/staff-kpi/staff/${values.user_id}${qs}`
      );
      setResult(response?.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      user_id: defaultUserId || '',
      from: defaultFrom || '',
      till: defaultTill || '',
    },
    enableReinitialize: true,
    onSubmit: fetchEarnings,
  });

  useEffect(() => {
    if (open && defaultUserId) {
      fetchEarnings({
        user_id: defaultUserId,
        from: defaultFrom || '',
        till: defaultTill || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultUserId]);

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
    setResult(null);
    setError('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      PaperProps={{ className: '!base-border !shadow-none' }}
    >
      <DialogTitle className='!bg-gray-100 !mb-2 flex items-center justify-between'>
        <span>{t('settings.staffKpi.earnings.title')}</span>
        <IconButton size='small' onClick={handleClose}>
          <i className='bi bi-x text-xl' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit} className='pt-2'>
          <Grid container spacing={2} rowSpacing={1} columns={{ xs: 12 }}>
            <Grid item xs={12} sm={4}>
              <FormSelectField
                delay={0}
                fieldName='user_id'
                label={t('common.fields.staff')}
                formik={formik}
                path='/dictionary/staffes'
                isitemsizesmall
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormDateField
                delay={0.05}
                label={t('common.table.from')}
                fieldName='from'
                formik={formik}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormDateField
                delay={0.1}
                label={t('common.table.till')}
                fieldName='till'
                formik={formik}
              />
            </Grid>
            <Grid item xs={12} className='flex justify-end gap-2'>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                disabled={!formik.values.user_id || loading}
                startIcon={loading ? <CircularProgress size={14} /> : <i className='bi bi-search' />}
              >
                {t('common.global.search')}
              </Button>
            </Grid>
          </Grid>
        </form>

        {error && (
          <div className='mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600'>
            {error}
          </div>
        )}

        {result && (
          <div className='mt-4 flex flex-col gap-3'>
            {/* User header */}
            <div className='flex items-center gap-3 rounded-xl bg-indigo-50 px-4 py-3'>
              <div className='w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold'>
                {result?.user?.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className='flex flex-col'>
                <span className='font-semibold text-gray-800'>{result?.user?.name}</span>
                <span className='text-xs text-gray-500'>{result?.user?.login}</span>
              </div>
            </div>

            {/* Items */}
            {result?.items?.length ? (
              <div className='flex flex-col gap-2'>
                {result.items.map((item, idx) => (
                  <div key={idx} className='flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 bg-white'>
                    <div className='flex flex-col gap-0.5 min-w-0 flex-1'>
                      <span className='font-medium text-sm text-gray-800 truncate'>
                        {item?.kpi?.type?.name || `#${item?.kpi?.type_id}`}
                      </span>
                      <span className='text-xs text-gray-500 flex items-center gap-1'>
                        {item?.kpi?.count_type_text} ·
                        <NumericFormat
                          value={item?.kpi?.price}
                          displayType='text'
                          thousandSeparator={item?.kpi?.count_type === 2 ? ' ' : ''}
                          decimalScale={2}
                        />
                        {item?.kpi?.count_type === 1 ? '%' : ''}
                      </span>
                    </div>
                    <div className='flex flex-col items-end text-sm font-medium shrink-0'>
                      {item?.earned_sum > 0 && <PriceCell value={item.earned_sum} suffix='UZS' />}
                      {item?.earned_valute > 0 && <PriceCell value={item.earned_valute} suffix='$' />}
                      {!item?.earned_sum && !item?.earned_valute && (
                        <span className='text-gray-400 text-xs'>—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='rounded-xl bg-gray-50 px-4 py-6 text-center text-gray-400 text-sm'>
                <i className='bi bi-exclamation-octagon text-xl mr-2' />
                {t('common.global.noDataFound')}
              </div>
            )}

            {/* Totals */}
            <div className='flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3'>
              <span className='font-medium text-emerald-700'>
                {t('common.table.total')}:
              </span>
              <div className='flex flex-col items-end font-semibold text-emerald-700'>
                {result?.total_sum > 0 && <PriceCell value={result.total_sum} suffix='UZS' />}
                {result?.total_valute > 0 && <PriceCell value={result.total_valute} suffix='$' />}
                {!result?.total_sum && !result?.total_valute && (
                  <PriceCell value={0} suffix='UZS' />
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default memo(EarningsModal);
